import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  ShoppingCart,
  Mail,
  X,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  User,
} from "lucide-react";
import api from "../lib/api";
import { ClientCombobox } from "../components/ClientCombobox";
import { Badge } from "../components/ui/badge";
import { useDebouncedCallback } from 'use-debounce';

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];
const STATUS_LABELS = {
  pending: "In Attesa",
  confirmed: "Confermato",
  shipped: "Spedito",
  delivered: "Consegnato",
  cancelled: "Annullato",
};

export default function OrdersPage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [emailData, setEmailData] = useState({ to_email: "", cc_emails: [] });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 15;

  const [formData, setFormData] = useState({
    client_id: "",
    items: [{ product_id: "", code: "", name: "", quantity: 1, price: 0, discount: 0, excise_duty: 0, stamp_duty: 0 }],
    total_amount: 0,
    status: "pending",
    additional_discount: 0,
    notes: "",
  });

  // Debounced search
  const debouncedSearch = useDebouncedCallback((value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, 400);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, agentFilter, searchTerm, currentPage]);

  useEffect(() => {
    fetchClients();
    fetchProducts();
    if (isAdmin) {
      fetchAgents();
    }
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", currentPage.toString());
      params.append("limit", itemsPerPage.toString());
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (agentFilter && agentFilter !== "all") params.append("agent_id", agentFilter);
      if (searchTerm) params.append("search", searchTerm);
      
      const response = await api.get(`/orders?${params}`);
      setOrders(response.data.items || []);
      setTotalPages(response.data.pages || 1);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      toast.error("Errore nel caricamento degli ordini");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    if (user?.role === "client") return;
    try {
      const response = await api.get("/clients");
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get("/agents");
      setAgents(response.data);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get("/products");
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const calculateTotal = (items, globalDiscount = 0) => {
    let subtotal = 0;
    let totalTax = 0;
    
    items.forEach(item => {
      const basePrice = item.price || 0;
      const qty = item.quantity || 0;
      const discount = item.discount || 0;
      const excise = item.excise_duty || 0;
      const stamp = item.stamp_duty || 0;
      
      const discountedPrice = basePrice * (1 - discount / 100);
      const lineTotal = discountedPrice * qty;
      const finalLineTotal = lineTotal * (1 - globalDiscount / 100);
      
      subtotal += finalLineTotal;
      totalTax += (excise + stamp) * qty;
    });
    
    const taxable = subtotal + totalTax;
    const vat = taxable * 0.22;
    
    return taxable + vat;
  };

  const updateFormData = (newItems, newGlobalDiscount) => {
    const total = calculateTotal(newItems, newGlobalDiscount !== undefined ? newGlobalDiscount : formData.additional_discount);
    setFormData({
      ...formData,
      items: newItems,
      additional_discount: newGlobalDiscount !== undefined ? newGlobalDiscount : formData.additional_discount,
      total_amount: total
    });
  };

  const handleProductSelect = (index, productId) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    const newItems = [...formData.items];
    newItems[index] = {
      product_id: product.id,
      code: product.code,
      name: product.name,
      quantity: newItems[index].quantity || 1,
      price: product.commercial_price,
      discount: 0,
      excise_duty: product.excise_duty || 0,
      stamp_duty: product.stamp_duty || 0
    };
    updateFormData(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    updateFormData(newItems);
  };

  const addItem = () => {
    const newItems = [...formData.items, { product_id: "", code: "", name: "", quantity: 1, price: 0, discount: 0, excise_duty: 0, stamp_duty: 0 }];
    updateFormData(newItems);
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    updateFormData(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOrder) {
        await api.put(`/orders/${editingOrder.id}`, formData);
        toast.success("Ordine aggiornato con successo");
      } else {
        await api.post("/orders", formData);
        toast.success("Ordine creato con successo");
      }
      setDialogOpen(false);
      setEditingOrder(null);
      resetForm();
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: user?.role === "client" ? user.client_id : "",
      items: [{ product_id: "", code: "", name: "", quantity: 1, price: 0, discount: 0, excise_duty: 0, stamp_duty: 0 }],
      total_amount: 0,
      status: "pending",
      additional_discount: 0,
      notes: "",
    });
  };

  const handleView = (id) => {
    navigate(`/orders/${id}`);
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      client_id: order.client_id,
      items: order.items?.length > 0 
        ? order.items 
        : [{ product_id: "", code: "", name: "", quantity: 1, price: 0, discount: 0, excise_duty: 0, stamp_duty: 0 }],
      total_amount: order.total_amount,
      status: order.status,
      additional_discount: order.additional_discount || 0,
      notes: order.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo ordine?")) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Ordine eliminato");
      fetchOrders();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}/status?status=${newStatus}`);
      toast.success("Stato aggiornato");
      fetchOrders();
    } catch (error) {
      toast.error("Errore nell'aggiornamento dello stato");
    }
  };

  const openEmailDialog = (order) => {
    const client = clients.find((c) => c.id === order.client_id);
    setSelectedOrder(order);
    setEmailData({ to_email: client?.email || "", cc_emails: [] });
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!selectedOrder) return;
    try {
      await api.post(`/orders/${selectedOrder.id}/send-email`, emailData);
      toast.success("Email inviata con successo");
      setEmailDialogOpen(false);
    } catch (error) {
      toast.error("Errore nell'invio dell'email");
    }
  };

  const getClientName = (clientId) => {
    if (user?.role === "client") return user.name; 
    const client = clients.find((c) => c.id === clientId);
    return client?.company_name || "N/A";
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  // Formattazione speciale per contrassegno con 3 decimali
  const formatStampDuty = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(value);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-primary font-bold">Ordini</h1>
          <p className="text-muted-foreground">
            {totalItems > 0 ? `${totalItems} ordini totali` : "Gestisci gli ordini dei clienti"}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-accent hover:bg-accent/90"
              onClick={() => {
                setEditingOrder(null);
                resetForm();
              }}
              data-testid="add-order-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Ordine
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingOrder ? `Modifica Ordine ${editingOrder.order_number}` : "Nuovo Ordine"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {user?.role !== "client" && (
                  <div className="space-y-2">
                    <Label>Cliente *</Label>
                    <ClientCombobox 
                      clients={clients} 
                      value={formData.client_id} 
                      onChange={(value) => setFormData({ ...formData, client_id: value })} 
                    />
                  </div>
                )}
                {user?.role !== "client" && (
                  <div className="space-y-2">
                    <Label>Stato</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) =>
                        setFormData({ ...formData, status: value })
                      }
                    >
                      <SelectTrigger data-testid="order-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {STATUS_LABELS[status]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Prodotti</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="w-4 h-4 mr-1" />
                    Aggiungi
                  </Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[250px]">Prodotto</TableHead>
                        <TableHead className="w-20">Qtà</TableHead>
                        <TableHead className="w-24">Prezzo</TableHead>
                        <TableHead className="w-20">Sc.%</TableHead>
                        <TableHead className="w-24 text-right">Accisa/C.</TableHead>
                        <TableHead className="w-28 text-right">Totale</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Select
                              value={item.product_id}
                              onValueChange={(value) => handleProductSelect(index, value)}
                            >
                              <SelectTrigger data-testid={`item-product-${index}`}>
                                <SelectValue placeholder="Seleziona prodotto">
                                  {item.name ? `${item.code} - ${item.name}` : "Seleziona prodotto"}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {products.map((product) => (
                                  <SelectItem key={product.id} value={product.id}>
                                    {product.code} - {product.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) =>
                                handleItemChange(index, "quantity", parseInt(e.target.value) || 0)
                              }
                              min="1"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="text-sm font-mono">{formatCurrency(item.price)}</div>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.discount}
                              onChange={(e) =>
                                handleItemChange(index, "discount", parseFloat(e.target.value) || 0)
                              }
                              min="0"
                              max="100"
                            />
                          </TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">
                             {(item.excise_duty > 0 || item.stamp_duty > 0) ? (
                                <div>
                                  {item.excise_duty > 0 && <div>A: {formatCurrency(item.excise_duty)}</div>}
                                  {item.stamp_duty > 0 && <div>C: {formatStampDuty(item.stamp_duty)}</div>}
                                </div>
                             ) : "-"}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(item.quantity * item.price * (1 - (item.discount || 0)/100))}
                          </TableCell>
                          <TableCell>
                            {formData.items.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeItem(index)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                <div className="flex justify-end gap-6 items-center pt-2">
                {user?.role !== "client" && (
                   <div className="flex items-center gap-2">
                      <Label>Sconto Extra Ordine %</Label>
                      <Input
                        type="number"
                        className="w-20 text-right"
                        value={formData.additional_discount}
                        onChange={(e) => updateFormData(formData.items, parseFloat(e.target.value) || 0)}
                        min="0"
                        max="100"
                      />
                   </div>
                )}
                   <div className="text-right text-lg font-medium">
                      Totale (Stimato): <span className="text-accent">{formatCurrency(formData.total_amount)}</span>
                   </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90">
                  {editingOrder ? "Aggiorna" : "Crea"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per numero ordine o cliente..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  debouncedSearch(e.target.value);
                }}
                className="pl-10"
                data-testid="search-orders-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Select value={agentFilter} onValueChange={(v) => { setAgentFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-full sm:w-44" data-testid="filter-agent">
                  <SelectValue placeholder="Agente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli agenti</SelectItem>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessun ordine trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>N. Ordine</TableHead>
                    <TableHead>Cliente</TableHead>
                    {user?.role !== "client" && <TableHead className="hidden lg:table-cell">Agente</TableHead>}
                    <TableHead className="hidden md:table-cell">Canale</TableHead>
                    <TableHead>Totale</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow 
                      key={order.id} 
                      data-testid={`order-row-${order.id}`}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleView(order.id)}
                    >
                      <TableCell className="font-mono text-sm">
                        {order.order_number}
                      </TableCell>
                      <TableCell>{order.client_name || getClientName(order.client_id)}</TableCell>
                      {user?.role !== "client" && (
                        <TableCell className="hidden lg:table-cell">
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <User className="w-3 h-3" />
                            {order.agent_name || "N/A"}
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="hidden md:table-cell">
                        <span className={`channel-badge channel-${order.channel?.toLowerCase().replace(".", "").replace(" ", "")}`}>
                          {order.channel}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono">
                        {formatCurrency(order.total_amount)}
                      </TableCell>
                      <TableCell>
                        {user?.role === "client" ? (
                          <Badge variant="outline" className={`status-badge status-${order.status} px-2 py-1`}>
                            {STATUS_LABELS[order.status]}
                          </Badge>
                        ) : (
                          <Select
                            value={order.status}
                            onValueChange={(value) => handleStatusChange(order.id, value)}
                          >
                            <SelectTrigger
                              className={`w-32 h-8 status-badge status-${order.status}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((status) => (
                                <SelectItem key={status} value={status}>
                                  {STATUS_LABELS[status]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleView(order.id)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Visualizza
                            </DropdownMenuItem>
                            {user?.role !== "client" && (
                              <DropdownMenuItem onClick={() => handleEdit(order)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Modifica
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => openEmailDialog(order)}>
                              <Mail className="w-4 h-4 mr-2" />
                              Invia Email
                            </DropdownMenuItem>
                            {user?.role !== "client" && (
                              <DropdownMenuItem
                                onClick={() => handleDelete(order.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Elimina
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-muted-foreground">
                Pagina {currentPage} di {totalPages} ({totalItems} risultati)
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-2 text-sm">{currentPage}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invia Conferma Ordine</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email destinatario</Label>
              <Input
                type="email"
                value={emailData.to_email}
                onChange={(e) => setEmailData({ ...emailData, to_email: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleSendEmail} className="bg-accent hover:bg-accent/90">
                <Mail className="w-4 h-4 mr-2" />
                Invia
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
